<template>
  <div class="flex flex-col w-full">
    <div
      v-if="initLoading"
      class="w-full h-full flex items-center justify-center"
    >
      <b-spinner
        class="ml-2"
        variant="primary"
        size="xl"
      />
    </div>

    <template v-else>
      <div
        v-if="step === 'step-1'"
        class="flex flex-col bg-white w-full border border-[#D7D7D7] shadow-[0_1px_3px_0_rgba(0,0,0,0.1)] rounded-[5px] px-[40px] py-[60px] pb-[90px]"
      >
        <div
          class="flex flex-col items-center text-center max-w-[800px] mx-auto"
        >
          <h2 class="text-3xl text-black mb-3">
            Looks like you haven’t setup a custom domain yet!
          </h2>
          <p class="text-lg text-[#6E747A] mb-4">
            Let's create your custom web address. It's easy to setup a custom domain with ESTAGE!
          </p>

          <img
            src="@builder/assets/svg/domain-setup.svg"
            class="mb-4 w-[400px] h-[400px]"
            alt="Domain Setup"
          >

          <div class="flex flex-col gap-y-1 text-start w-full xl:max-w-[600px]">
            <div class="text-lg uppercase font-bold text-[#6E747A] mb-1">
              STEP 1: Add Your Domain
            </div>
            <p class="text-base text-[15px] text-[#6E747A] mb-[24px]">
              You can register a new domain on <a
                href="https://godaddy.com"
                target="_blank"
              >GoDaddy.com</a> or <a
                href="https://www.namecheap.com"
                target="_blank"
              >Namecheap.com</a> or at any other domain registrar of your choice. (we recommend GoDaddy or Namecheap as they’re the most popular and offer the most flexibility and tools) Once you’ve registered a domain, your next step is to connect it to your ESTAGE project by entering it below.
            </p>
            <div class="flex gap-x-4">
              <b-input
                v-model="domain"
                v-validate="'required|domain'"
                class="border w-full !h-[40px]"
                placeholder="Enter your domain name (example.com)"
                name="domain"
                :state="errors.has('domain') ? false : null"
                autocomplete="off"
                :disabled="!canCreateDomainManagement"
              />
              <b-btn
                variant="primary"
                class="h-[40px] min-w-[160px]"
                :disabled="loading || !canCreateDomainManagement"
                @click="addDomain"
              >
                {{ project.domain === domain ? 'Continue' : 'Add Domain' }}
                <b-spinner
                  v-if="loading"
                  class="ml-2"
                  variant="white"
                  small
                />
              </b-btn>
              <delete-confirm
                v-if="project.domain && !loading && canDeleteDomainManagement"
                title="Are you sure?"
                description="The domain will be deleted, the action cannot be undone."
                @confirm="deleteDomain"
              >
                <template #activator="{on}">
                  <b-btn
                    variant="danger"
                    class="h-[40px] min-w-[160px]"
                    :disabled="loading"
                    v-on="on"
                  >
                    Delete Domain
                  </b-btn>
                </template>
              </delete-confirm>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="step === 'step-2'"
        class="relative flex flex-col bg-white w-full border border-[#D7D7D7] shadow-[0_1px_3px_0_rgba(0,0,0,0.1)] rounded-[5px] px-[40px] py-[60px] pb-[90px]"
      >
        <button
          class="absolute top-[30px] left-[40px] text-base text-[#6E747A] hover:text-black transition-colors p-0"
          @click="() => step = 'step-1'"
        >
          <i class="fa fa-angle-left text-xs mr-1" />
          Back
        </button>

        <div
          class="flex flex-col items-center text-center max-w-[800px] mx-auto"
        >
          <h3 class="text-lg uppercase font-semibold text-[#6E747A] mb-2">
            Step 2
          </h3>
          <h2 class="text-3xl text-black mb-3">
            Set Nameservers
          </h2>

          <div class="w-full text-start">
            <p class="text-lg text-[#6E747A] mb-12 max-w-[600px] text-center mx-auto">
              To start using ESTAGE DNS, you will need to configure your domain with your designated (and unique) nameservers listed below. The actual configuration will depend on your domain registrar.
            </p>

            <h3 class="text-base text-[#6E747A]">
              1. Log in to your registrar account
            </h3>
            <p class="text-sm text-[#6E747A] mb-6">
              The first step is to log in to your account with your registrar where you registered your domain name.
            </p>

            <h3 class="text-base text-[#6E747A]">
              2. Remove your current nameservers
            </h3>
            <p class="text-sm text-[#6E747A] mb-6">
              Next, locate the nameserver configuration section and remove your currently configured nameservers.
            </p>

            <h3 class="text-base text-[#6E747A]">
              3. Add DNS servers
            </h3>
            <p class="text-sm text-[#6E747A] mb-6">
              Finally, configure your domain with the four nameservers below.
            </p>
          </div>

          <div class="w-full grid grid-cols-2 gap-y-5 gap-x-4 mb-5">
            <!--              v-for="(item, index) in isSuperAdmin || isSupport ? awsNS : nameserver"-->
            <div
              v-for="(item, index) in awsNS"
              :key="index"
              class="relative w-full"
            >
              <b-input-group
                :prepend="`Nameserver ${index + 1}`"
                class="h-[40px] relative"
              >
                <b-form-input
                  :value="item.name.slice(0, -1)"
                  class="bg-white !h-[40px] !pr-[40px] text-truncate"
                  readonly
                />
              </b-input-group>

              <!--                @click="copyNameServer(index, isSuperAdmin || isSupport ? awsNS : nameserver)"-->
              <button
                class="absolute top-1/2 right-[14px] -translate-y-1/2 p-0"
                @click="copyNameServer(index, awsNS)"
              >
                <svg
                  v-if="!item.copied"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-copy-icon lucide-copy w-4 h-4"
                >
                  <rect
                    width="14"
                    height="14"
                    x="8"
                    y="8"
                    rx="2"
                    ry="2"
                  />
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>

                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-copy-check-icon lucide-copy-check w-4 h-4"
                >
                  <path d="m12 15 2 2 4-4" />
                  <rect
                    width="14"
                    height="14"
                    x="8"
                    y="8"
                    rx="2"
                    ry="2"
                  />
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>
              </button>
            </div>
          </div>

          <div class="w-full flex justify-between border border-[#D7D7D7] shadow-[0_1px_3px_0_rgba(0,0,0,0.1)] rounded-[5px] bg-white px-4 py-[20px]">
            <div class="flex gap-x-2.5 items-center">
              <p class="text-base text-[#515151] font-semibold mb-0">
                Nameservers Status:
              </p>

              <template v-if="!verify">
                <div class="flex flex-col gap-[2px] max-w-[400px] mr-3">
                  <p class="text-base leading-none text-[#515151] mb-0 text-start">
                    Not connected.
                  </p>
                  <span class="text-xs leading-normal text-[#515151] mb-0 text-start">
                    (If you've set your nameservers, please allow up to 30min - 4hrs  for your domain DNS to propagate. Click the refresh wheel to check the status.)
                  </span>
                </div>
              </template>

              <template v-else>
                <span class="w-1.5 h-1.5 bg-[#F4CB44] rounded-full animate-pulse" />

                <div class="flex flex-col gap-[2px]">
                  <p class="text-base leading-none text-[#515151] mb-0 text-start">
                    Ready for connection
                  </p>
                </div>
              </template>
            </div>

            <button
              ref="checkBtn"
              class="p-0"
              @click="checkDomain"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.33325 6.66667C1.33325 6.66667 1.41413 6.10051 3.75728 3.75736C6.10042 1.41421 9.89941 1.41421 12.2426 3.75736C13.0727 4.58754 13.6088 5.60048 13.8507 6.66667M1.33325 6.66667V2.66667M1.33325 6.66667H5.33325M14.6666 9.33333C14.6666 9.33333 14.5857 9.8995 12.2426 12.2426C9.89941 14.5858 6.10042 14.5858 3.75728 12.2426C2.9271 11.4125 2.39105 10.3995 2.14914 9.33333M14.6666 9.33333V13.3333M14.6666 9.33333H10.6666"
                  stroke="#515151"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <b-btn
            v-if="verify && !pendingSetup && canCreateDomainManagement"
            variant="primary"
            class="mt-9 h-[40px]"
            size="md"
            @click="connectDomainAdmin"
          >
            Connect Domain
            <b-spinner
              v-if="loading"
              class="ml-2"
              variant="white"
              small
            />
          </b-btn>
        </div>
      </div>

      <template v-else-if="step === 'management'">
        <div class="bg-white w-full border border-[#D7D7D7] shadow-[0_1px_3px_0_rgba(0,0,0,0.1)] p-8 rounded-[5px]">
          <div class="flex items-center gap-2">
            <h3 class="text-lg font-semibold my-auto">
              Linked Domains/Sub-domains
            </h3>

            <span class="inline-flex items-center justify-center h-[24px] px-[8px] py-[4px] rounded-[4px] text-[12px] leading-[14px] font-[400] capitalize bg-[#EBF2FF] text-[#4F83E3]">
              {{ awsConnected ? 'AWS' : 'Bunny' }}
            </span>
          </div>

          <table class="border-collapse table-auto w-full text-base mt-4">
            <thead>
              <tr>
                <th
                  v-for="(item, index) in tableHead"
                  :key="index"
                  class="h-[40px] bg-[#F9F9F9] px-[20px] py-[10px]"
                >
                  {{ item }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white">
              <tr
                v-for="(item, index) in records"
                :key="index"
                class="border-t border-[#D7D7D7]"
              >
                <td class="px-[20px] py-[10px]">
                  {{ item.Name.length === 0 ? project.domain : `${item.Name}.${project.domain}` }}
                </td>
                <td class="px-[20px] py-[10px] text-sm">
                  {{ item.ModuleType }}
                </td>
                <td class="px-[20px] py-[10px]">
                  <div
                    class="w-fit h-[24px] px-[12px] rounded text-xs font-semibold uppercase flex items-center"
                    :class="[item.Ssl ? 'bg-green-100 text-green-500' : 'bg-gray-100 text-gray-500']"
                  >
                    {{ item.Ssl ? 'Enabled' : 'Pending' }}
                  </div>
                </td>
                <td class="px-[20px] py-[10px] flex justify-end">
                  <b-dropdown
                    v-if="item.Name !== 'www' && (canUpdateDomainManagement || canDeleteDomainManagement)"
                    toggle-class="p-0 bg-transparent border-0 w-[24px] h-[24px]"
                    menu-class="bg-white p-0"
                    right
                  >
                    <template #button-content>
                      <div class="py-2">
                        <i class="fa fa-ellipsis-h" />
                      </div>
                    </template>

                    <delete-confirm
                      v-if="item.Name.length === 0 && canDeleteDomainManagement"
                      title="Are you sure?"
                      description="The domain will be deleted, the action cannot be undone."
                      @confirm="deleteDomain"
                    >
                      <template #activator="{on}">
                        <b-dropdown-item v-on="on">
                          <div class="flex items-center gap-[6px]">
                            <svg
                              width="18"
                              height="19"
                              viewBox="0 0 18 19"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              class="w-[16px] h-[16px]"
                            >
                              <path
                                d="M6.74988 2.75H11.2499M2.24988 5H15.7499M14.2499 5L13.7239 12.8895C13.645 14.0732 13.6055 14.665 13.3499 15.1138C13.1248 15.5088 12.7853 15.8265 12.3761 16.0248C11.9114 16.25 11.3182 16.25 10.1319 16.25H7.86787C6.68156 16.25 6.0884 16.25 5.62363 16.0248C5.21445 15.8265 4.87494 15.5088 4.64987 15.1138C4.39421 14.665 4.35475 14.0732 4.27584 12.8895L3.74988 5M7.49988 8.375V12.125M10.4999 8.375V12.125"
                                stroke="currentColor"
                                stroke-width="1.2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                            Delete
                          </div>
                        </b-dropdown-item>
                      </template>
                    </delete-confirm>

                    <template v-else>
                      <b-dropdown-item
                        v-if="canUpdateDomainManagement"
                        @click="editItem(item)"
                      >
                        <div class="flex items-center gap-[6px]">
                          <svg
                            width="18"
                            height="19"
                            viewBox="0 0 18 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-[16px] h-[16px]"
                          >
                            <path
                              d="M9 11.75C10.2426 11.75 11.25 10.7426 11.25 9.5C11.25 8.25736 10.2426 7.25 9 7.25C7.75736 7.25 6.75 8.25736 6.75 9.5C6.75 10.7426 7.75736 11.75 9 11.75Z"
                              stroke="currentColor"
                              stroke-width="1.2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M14.0455 11.5455C13.9547 11.7511 13.9276 11.9792 13.9677 12.2004C14.0078 12.4216 14.1133 12.6257 14.2705 12.7864L14.3114 12.8273C14.4382 12.9539 14.5387 13.1043 14.6074 13.2699C14.676 13.4354 14.7113 13.6128 14.7113 13.792C14.7113 13.9712 14.676 14.1487 14.6074 14.3142C14.5387 14.4798 14.4382 14.6302 14.3114 14.7568C14.1847 14.8836 14.0343 14.9842 13.8688 15.0528C13.7032 15.1214 13.5258 15.1568 13.3466 15.1568C13.1674 15.1568 12.9899 15.1214 12.8244 15.0528C12.6589 14.9842 12.5085 14.8836 12.3818 14.7568L12.3409 14.7159C12.1802 14.5587 11.9761 14.4533 11.7549 14.4132C11.5338 14.3731 11.3056 14.4001 11.1 14.4909C10.8983 14.5773 10.7264 14.7208 10.6052 14.9038C10.4841 15.0867 10.4191 15.3011 10.4182 15.5205V15.6364C10.4182 15.998 10.2745 16.3449 10.0188 16.6006C9.76305 16.8563 9.4162 17 9.05455 17C8.69289 17 8.34604 16.8563 8.09031 16.6006C7.83458 16.3449 7.69091 15.998 7.69091 15.6364V15.575C7.68563 15.3493 7.61258 15.1305 7.48126 14.9468C7.34993 14.7632 7.16641 14.6234 6.95455 14.5455C6.7489 14.4547 6.52078 14.4276 6.2996 14.4677C6.07842 14.5078 5.87433 14.6133 5.71364 14.7705L5.67273 14.8114C5.54608 14.9382 5.39569 15.0387 5.23015 15.1074C5.0646 15.176 4.88716 15.2113 4.70795 15.2113C4.52875 15.2113 4.35131 15.176 4.18576 15.1074C4.02022 15.0387 3.86983 14.9382 3.74318 14.8114C3.6164 14.6847 3.51582 14.5343 3.44719 14.3688C3.37857 14.2032 3.34325 14.0258 3.34325 13.8466C3.34325 13.6674 3.37857 13.4899 3.44719 13.3244C3.51582 13.1589 3.6164 13.0085 3.74318 12.8818L3.78409 12.8409C3.94128 12.6802 4.04672 12.4761 4.08682 12.2549C4.12693 12.0338 4.09985 11.8056 4.00909 11.6C3.92266 11.3983 3.77915 11.2264 3.59623 11.1052C3.4133 10.9841 3.19895 10.9191 2.97955 10.9182H2.86364C2.50198 10.9182 2.15513 10.7745 1.8994 10.5188C1.64367 10.2631 1.5 9.9162 1.5 9.55455C1.5 9.19289 1.64367 8.84604 1.8994 8.59031C2.15513 8.33458 2.50198 8.19091 2.86364 8.19091H2.925C3.15068 8.18563 3.36955 8.11258 3.55316 7.98126C3.73677 7.84993 3.87662 7.66641 3.95455 7.45455C4.04531 7.2489 4.07238 7.02078 4.03228 6.7996C3.99217 6.57842 3.88673 6.37433 3.72955 6.21364L3.68864 6.17273C3.56185 6.04608 3.46127 5.89569 3.39265 5.73015C3.32402 5.5646 3.2887 5.38716 3.2887 5.20795C3.2887 5.02875 3.32402 4.85131 3.39265 4.68576C3.46127 4.52022 3.56185 4.36983 3.68864 4.24318C3.81528 4.1164 3.96568 4.01582 4.13122 3.94719C4.29676 3.87857 4.47421 3.84325 4.65341 3.84325C4.83261 3.84325 5.01006 3.87857 5.1756 3.94719C5.34114 4.01582 5.49154 4.1164 5.61818 4.24318L5.65909 4.28409C5.81978 4.44128 6.02387 4.54672 6.24505 4.58682C6.46623 4.62693 6.69435 4.59985 6.9 4.50909H6.95455C7.15621 4.42266 7.32819 4.27915 7.44934 4.09623C7.57048 3.9133 7.63549 3.69895 7.63636 3.47955V3.36364C7.63636 3.00198 7.78003 2.65513 8.03576 2.3994C8.2915 2.14367 8.63834 2 9 2C9.36166 2 9.7085 2.14367 9.96424 2.3994C10.22 2.65513 10.3636 3.00198 10.3636 3.36364V3.425C10.3645 3.6444 10.4295 3.85876 10.5507 4.04168C10.6718 4.22461 10.8438 4.36812 11.0455 4.45455C11.2511 4.54531 11.4792 4.57238 11.7004 4.53228C11.9216 4.49217 12.1257 4.38673 12.2864 4.22955L12.3273 4.18864C12.4539 4.06185 12.6043 3.96127 12.7699 3.89265C12.9354 3.82402 13.1128 3.7887 13.292 3.7887C13.4712 3.7887 13.6487 3.82402 13.8142 3.89265C13.9798 3.96127 14.1302 4.06185 14.2568 4.18864C14.3836 4.31528 14.4842 4.46568 14.5528 4.63122C14.6214 4.79676 14.6568 4.97421 14.6568 5.15341C14.6568 5.33261 14.6214 5.51006 14.5528 5.6756C14.4842 5.84114 14.3836 5.99154 14.2568 6.11818L14.2159 6.15909C14.0587 6.31978 13.9533 6.52387 13.9132 6.74505C13.8731 6.96623 13.9001 7.19435 13.9909 7.4V7.45455C14.0773 7.65621 14.2208 7.82819 14.4038 7.94934C14.5867 8.07048 14.8011 8.13549 15.0205 8.13636H15.1364C15.498 8.13636 15.8449 8.28003 16.1006 8.53576C16.3563 8.7915 16.5 9.13834 16.5 9.5C16.5 9.86166 16.3563 10.2085 16.1006 10.4642C15.8449 10.72 15.498 10.8636 15.1364 10.8636H15.075C14.8556 10.8645 14.6412 10.9295 14.4583 11.0507C14.2754 11.1718 14.1319 11.3438 14.0455 11.5455Z"
                              stroke="currentColor"
                              stroke-width="1.2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          Edit
                        </div>
                      </b-dropdown-item>
                    </template>
                  </b-dropdown>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-if="!awsConnected && canUpdateDomainManagement"
          class="flex justify-center mt-8"
        >
          <b-btn
            variant="primary"
            size="lg"
            class="h-[40px] !text-base"
            :disabled="loading"
            @click="upgradeAWS"
          >
            Migrate to AWS
          </b-btn>
        </div>

        <div
          v-if="awsConnected && !awsDnsMigrated && canUpdateDomainManagement"
          class="flex justify-center mt-8"
        >
          <b-btn
            variant="primary"
            size="lg"
            class="h-[40px] !text-base"
            :disabled="loading"
            @click="upgradeAWSDNS"
          >
            Upgrade to AWS (Route 53) DNS!
          </b-btn>
        </div>
      </template>
    </template>

    <AwsUpgradeModal
      v-model="awsUpgradeModal"
      :project="project"
      :by-project="true"
      @success="setupAws"
    />

    <b-modal
      v-model="awsDNSModal"
      size="lg"
      dialog-class="rounded-[10px]"
      body-class="!px-[32px] !py-[3rem]"
      hide-header
      hide-footer
      centered
      lazy
      no-close-on-backdrop
      no-close-on-esc
    >
      <i
        class="fa fa-close z-[50] absolute top-[15px] right-[17px] text-[16px] text-[#6E747A] transition cursor-pinter hover:opacity-60"
        @click="awsDNSModal = false"
      />

      <div class="flex flex-col w-full min-h-[500px] text-center">
        <h2 class="text-3xl text-black mb-3">
          Set Nameservers
        </h2>

        <div class="w-full text-start">
          <p class="text-lg text-[#6E747A] mb-12 max-w-[600px] text-center mx-auto">
            To start using ESTAGE DNS, you will need to configure your domain with your designated (and unique) nameservers listed below. The actual configuration will depend on your domain registrar.
          </p>

          <h3 class="text-base text-[#6E747A]">
            1. Log in to your registrar account
          </h3>
          <p class="text-sm text-[#6E747A] mb-6">
            The first step is to log in to your account with your registrar where you registered your domain name.
          </p>

          <h3 class="text-base text-[#6E747A]">
            2. Remove your current nameservers
          </h3>
          <p class="text-sm text-[#6E747A] mb-6">
            Next, locate the nameserver configuration section and remove your currently configured nameservers.
          </p>

          <h3 class="text-base text-[#6E747A]">
            3. Add DNS servers
          </h3>
          <p class="text-sm text-[#6E747A] mb-6">
            Finally, configure your domain with the four nameservers below.
          </p>
        </div>

        <div class="w-full grid grid-cols-2 gap-y-5 gap-x-4 mb-5">
          <div
            v-for="(item, index) in awsNS"
            :key="index"
            class="relative w-full"
          >
            <b-input-group
              :prepend="`Nameserver ${index + 1}`"
              class="h-[40px] relative"
            >
              <b-form-input
                :value="item.name.replace(/\.$/, '')"
                readonly
                class="bg-white !h-[40px] !pr-[36px] text-truncate"
              />
            </b-input-group>

            <button
              class="absolute top-1/2 right-[14px] -translate-y-1/2 p-0"
              @click="copyNameServer(index, awsNS)"
            >
              <svg
                v-if="!item.copied"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-copy-icon lucide-copy w-4 h-4"
              >
                <rect
                  width="14"
                  height="14"
                  x="8"
                  y="8"
                  rx="2"
                  ry="2"
                />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>

              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-copy-check-icon lucide-copy-check w-4 h-4"
              >
                <path d="m12 15 2 2 4-4" />
                <rect
                  width="14"
                  height="14"
                  x="8"
                  y="8"
                  rx="2"
                  ry="2"
                />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
            </button>
          </div>
        </div>

        <div class="w-full flex justify-between border border-[#D7D7D7] shadow-[0_1px_3px_0_rgba(0,0,0,0.1)] rounded-[5px] bg-white px-4 py-[20px]">
          <div class="flex gap-x-2.5 items-center">
            <p class="text-base text-[#515151] font-semibold mb-0">
              Nameservers Status:
            </p>

            <template v-if="!awsVerified">
              <div class="flex flex-col gap-[2px] max-w-[400px] mr-3">
                <p class="text-base leading-none text-[#515151] mb-0 text-start">
                  Not connected.
                </p>
                <span class="text-xs leading-normal text-[#515151] mb-0 text-start">
                  (If you've set your nameservers, please allow up to 30min - 4hrs  for your domain DNS to propagate. Click the refresh wheel to check the status.)
                </span>
              </div>
            </template>

            <template v-else>
              <span class="w-1.5 h-1.5 bg-[#F4CB44] rounded-full animate-pulse" />

              <div class="flex flex-col gap-[2px]">
                <p class="text-base leading-none text-[#515151] mb-0 text-start">
                  Ready for connection
                </p>
              </div>
            </template>
          </div>

          <button
            ref="checkBtn"
            class="p-0"
            @click="checkDomain"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.33325 6.66667C1.33325 6.66667 1.41413 6.10051 3.75728 3.75736C6.10042 1.41421 9.89941 1.41421 12.2426 3.75736C13.0727 4.58754 13.6088 5.60048 13.8507 6.66667M1.33325 6.66667V2.66667M1.33325 6.66667H5.33325M14.6666 9.33333C14.6666 9.33333 14.5857 9.8995 12.2426 12.2426C9.89941 14.5858 6.10042 14.5858 3.75728 12.2426C2.9271 11.4125 2.39105 10.3995 2.14914 9.33333M14.6666 9.33333V13.3333M14.6666 9.33333H10.6666"
                stroke="#515151"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <div class="flex justify-center">
          <b-btn
            v-if="awsVerified"
            variant="primary"
            class="mt-9 h-[40px]"
            size="md"
            @click="connectAWSDNS"
          >
            Connect to AWS (Route 53) DNS!
            <b-spinner
              v-if="loading"
              class="ml-2"
              variant="white"
              small
            />
          </b-btn>
        </div>
      </div>
    </b-modal>
    
    <b-modal
      v-model="sslSetupModal"
      size="md"
      dialog-class="rounded-[10px]"
      body-class="!px-[32px] !py-[3rem]"
      hide-header
      hide-footer
      centered
      lazy
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="flex flex-col items-center justify-start py-[12px]">
        <div
          class="swal2-icon swal2-success swal2-icon-show"
          style="display: flex;"
        >
          <div
            class="swal2-success-circular-line-left"
            style="background-color: rgb(255, 255, 255);"
          />
          <span class="swal2-success-line-tip" /> <span class="swal2-success-line-long" />
          <div class="swal2-success-ring" /> <div
            class="swal2-success-fix"
            style="background-color: rgb(255, 255, 255);"
          />
          <div
            class="swal2-success-circular-line-right"
            style="background-color: rgb(255, 255, 255);"
          />
        </div>

        <p class="text-[20px] leading-normal text-center text-[#595959]">
          Please wait... we're setting up your SSL certificate for {{ sslHostname }}
        </p>
      </div>
    </b-modal>

    <b-modal
      v-model="setupDomainModal"
      size="md"
      dialog-class="rounded-[10px]"
      body-class="!p-[24px]"
      hide-header
      hide-footer
      centered
      lazy
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="flex flex-col items-center justify-start py-[12px]">
        <div
          class="swal2-icon swal2-success swal2-icon-show"
          style="display: flex;"
        >
          <div
            class="swal2-success-circular-line-left"
            style="background-color: rgb(255, 255, 255);"
          />
          <span class="swal2-success-line-tip" /> <span class="swal2-success-line-long" />
          <div class="swal2-success-ring" /> <div
            class="swal2-success-fix"
            style="background-color: rgb(255, 255, 255);"
          />
          <div
            class="swal2-success-circular-line-right"
            style="background-color: rgb(255, 255, 255);"
          />
        </div>

        <p class="text-[1.875rem] leading-none text-center text-[#595959] mt-3">
          Please hang tight... We're setting up your domain {{ loadingDots }}
        </p>

        <span class="text-2xl text-[#595959]">
          (This may take up to 90 seconds)
        </span>
      </div>
    </b-modal>

    <b-modal
      v-model="domainModal"
      size="lg"
      dialog-class="rounded-[10px]"
      body-class="!px-[32px] !py-[3rem]"
      hide-header
      hide-footer
      centered
      lazy
    >
      <div class="flex flex-col">
        <h2 class="text-xl text-center mb-8">
          {{ form.id || form.solid ? 'Update' : 'Add' }} Sub-domain
        </h2>

        <b-form-group label="Hostname">
          <b-input-group
            :append="`.${project.domain}`"
            class="input-hostname"
          >
            <b-form-input
              v-model="form.name"
              placeholder="leave empty for root"
              class="!h-[40px]"
            />
          </b-input-group>
        </b-form-group>

        <div class="inline-flex gap-x-[10px] mt-12">
          <b-btn
            v-if="form.id || form.solid"
            variant="primary"
            type="button"
            :disabled="loading"
            @click="submitRecord"
          >
            {{ form.id || form.solid ? 'Update' : 'Add' }}
            <b-spinner
              v-if="loading"
              class="ml-2"
              variant="white"
              small
            />
          </b-btn>
          <b-btn
            variant="secondary"
            type="button"
            :disabled="loading"
            @click="() => domainModal = false"
          >
            Cancel
          </b-btn>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import _ from 'lodash'
import {mapActions, mapGetters, mapState} from 'vuex'
// import DomainConnection from '@/views/domain/views/DomainConnection.vue'
// import DomainSetup from '@/views/domain/views/DomainSetup.vue'
// import DomainSubDomain from '@/views/domain/views/DomainSubDomain.vue'
// import SslSetupModal from '@/views/domain/modals/SslSetupModal.vue'
// import SetupDomainModal from '@/views/domain/modals/SetupDomainModal.vue'
// import DomainModal from '@/views/domain/modals/DomainModal.vue'
import AwsUpgradeModal from '@/components/editor/modals/AwsUpgradeModal.vue'
import DeleteConfirm from '@/components/editor/modals/DeleteConfirm.vue'
import RouterMixin from '@/components/mixins/RouterMixin'

export default {
  name: 'DomainManagement',
  components: {
    AwsUpgradeModal,
    DeleteConfirm
    // DomainConnection,
    // DomainSetup,
    // DomainSubDomain,
    // SslSetupModal,
    // SetupDomainModal,
    // DomainModal
  },
  mixins: [RouterMixin],
  data () {
    return {
      newAPI: 'https://new-api.estage.com',
      checkCertificateCount: 0,
      socialActivated: false,
      loading: true,
      initLoading: false,
      awsUpgradeModal: false,
      awsDNSModal: false,
      sslSetupModal: false,
      sslHostname: '',
      setupDomainModal: false,
      domainModal: false,
      loadingDots: '',
      dotsTimer: null,
      step: 'step-1',
      domain: '',
      domainSaved: false,
      pendingSetup: false,
      verify: false,
      awsVerified: false,
      awsDnsMigrated: false,
      nameserver: [
        {
          name: 'ns1.estage.site',
          copied: false
        },
        {
          name: 'ns2.estage.site',
          copied: false
        }
      ],
      awsNS: [],
      tableHead: ['Hostname', 'Type', 'SSL', ''],
      records: [],
      form: {
        id: null,
        name: '',
        value: '',
        port: null,
        priority: null,
        weight: 100,
        type: 0,
        ttl: 300,
        flags: 0,
        tag: '',
        monitor_type: 0,
        smart_routing_type: 0,
        enable: true
      },
      allTypes: [
        {
          name: 'A',
          value: 0,
          field: {
            name: 'IPv4 Address',
            placeholder: 'Example: 192.0.2.55'
          }
        },
        {
          name: 'AAAA',
          value: 1,
          field: {
            name: 'IPv6 Address',
            placeholder: 'Example: 2001:0db8:85a3:0000:0000:8a2e:0370:7334'
          }
        },
        {
          name: 'CNAME',
          value: 2,
          field: {
            name: 'Target',
            placeholder: 'www.example.com'
          }
        },
        {
          name: 'TXT',
          value: 3,
          field: {
            name: 'Content',
            placeholder: 'Example Text Entry'
          }
        },
        {
          name: 'MX',
          value: 4,
          field: {
            name: 'Mail Server',
            placeholder: 'mailserver.example.com'
          }
        },
        {
          name: 'RDR',
          value: 5,
          field: {
            name: 'URL',
            placeholder: 'https://mywebsite.com'
          }
        },
        {
          name: 'Flatten',
          value: 6
        },
        {
          name: 'PZ',
          value: 7
        },
        {
          name: 'SRV',
          value: 8,
          field: {
            name: 'Server Hostname',
            placeholder: 'xmpp-server.example.com'
          }
        },
        {
          name: 'CAA',
          value: 9,
          field: {
            name: 'Server Hostname',
            placeholder: 'caa.example.com'
          }
        },
        {
          name: 'PTR',
          value: 10,
          field: {
            name: 'Domain Name',
            placeholder: 'www.example.com'
          }
        },
        {
          name: 'SCR',
          value: 11
        },
        {
          name: 'NS',
          value: 12,
          field: {
            name: 'Target',
            placeholder: 'ns1.someserver.com'
          }
        }
      ]
    }
  },
  computed: {
    ...mapState('projects', {
      project: state => state.project
    }),
    ...mapGetters('auth', {
      user: 'user',
      isSuperAdmin: 'isSuperAdmin',
      isSupport: 'isSupport'
    }),
    ...mapGetters('projects', {
      checkPermissions: 'checkPermissions'
    }),
    canCreateDomainManagement () {
      return this.checkPermissions('domain_management.create')
    },
    canUpdateDomainManagement () {
      return this.checkPermissions('domain_management.update')
    },
    canDeleteDomainManagement () {
      return this.checkPermissions('domain_management.delete')
    },
    awsAccess () {
      return this.user.aws_access || this.project.aws_access
    },
    awsConnected () {
      return this.project?.aws_data?.certificate_arn
    }
  },
  methods: {
    ...mapActions({
      checkNS: 'domain/checkNS',
      fetchDNS: 'domain/fetchDNS'
    }),
    async refreshState () {
      await this.$store.dispatch('projects/refreshProject')
      this.domain = this.project.domain || ''
      this.domainSaved = !!this.project.domain
      if (this.project.domain) {
        await this.checkDomain()
      } else {
        this.step = 'step-1'
        this.records = []
        this.verify = false
        this.pendingSetup = false
      }
      this.loading = false
      this.initLoading = false
    },
    copyNameServer (index, list) {
      list[index].copied = true
      const name = list[index].name.replace(/\.$/, '')

      navigator.clipboard.writeText(name)

      setTimeout(() => {
        this.$swal({
          icon: 'success',
          iconColor: '#4F83E3',
          title: 'Copied to clipboard',
          showConfirmButton: false,
          position: 'top-right',
          toast: true,
          timer: 1500
        })
        list[index].copied = false
      }, 800)
    },
    startLoadingDots () {
      this.loadingDots = '.'
      this.dotsTimer = setInterval(() => {
        if (this.loadingDots.length >= 3) {
          this.loadingDots = '.'
        } else {
          this.loadingDots += '.'
        }
      }, 500)
    },
    stopLoadingDots () {
      clearInterval(this.dotsTimer)
      this.dotsTimer = null
      this.loadingDots = ''
    },
    async addDomain () {
      if (this.project.domain === this.domain) {
        const {data} = await axios.get(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/add-domain`)
        this.awsNS = []
        data.data.forEach(item => {
          this.awsNS.push({
            name: item,
            copied: false
          })
        })
        // if (this.isSuperAdmin || this.isSupport) {
        //   const {data} = await axios.get(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/add-domain`)
        //   this.awsNS = []
        //   data.data.forEach(item => {
        //     this.awsNS.push({
        //       name: item,
        //       copied: false
        //     })
        //   })
        // } else {
        //   await axios.post(`api/projects/${this.project.id}/bunny-dns`)
        // }
        this.step = 'step-2'
        window.scrollTo({top: 0, behavior: 'smooth'})
      } else {
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.loading = true
            this.project.domain = this.removeWWW(this.domain)

            this.$store.dispatch('projects/save', true)
              .then(async () => {
                const {data} = await axios.get(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/add-domain`)
                this.awsNS = []
                data.data.forEach(item => {
                  this.awsNS.push({
                    name: item,
                    copied: false
                  })
                })
                // if (this.isSuperAdmin || this.isSupport) {
                //   const {data} = await axios.get(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/add-domain`)
                //   this.awsNS = []
                //   data.data.forEach(item => {
                //     this.awsNS.push({
                //       name: item,
                //       copied: false
                //     })
                //   })
                // } else {
                //   await axios.post(`api/projects/${this.project.id}/bunny-dns`)
                // }

                this.domainSaved = true
                this.step = 'step-2'
                window.scrollTo({top: 0, behavior: 'smooth'})
                await this.checkDomain()
                this.loading = false
              })
              .catch(() => {
                this.loading = false
                this.project.domain = ''
                this.$swal({
                  icon: 'error',
                  iconColor: '#e34f5b',
                  toast: true,
                  position: 'top-right',
                  title: 'Sorry, that domain name is already in use - please check your other projects.',
                  showConfirmButton: false,
                  timer: 5000
                })
              })
          }
        })
      }
    },
    async deleteDomain () {
      const previousDomain = this.domain
      const previousProjectDomain = this.project.domain

      this.initLoading = true
      this.loading = true
      this.verify = false
      this.$validator.reset()

      try {
        await axios.delete(`api/projects/${this.project.id}/bunny-dns`)

        if (this.awsConnected) {
          await axios.delete(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/delete`)
        }

        this.domain = ''
        this.project.domain = ''
        this.project.aws_data = null

        await this.$store.dispatch('projects/save', true)

        this.$swal({
          icon: 'success',
          iconColor: '#4F83E3',
          toast: true,
          position: 'top-right',
          title: 'Domain has been deleted.',
          showConfirmButton: false,
          timer: 3000
        })
        await this.refreshState()
      } catch (error) {
        console.log(error)
        this.domain = previousDomain
        this.project.domain = previousProjectDomain
        this.loading = false
        this.initLoading = false
      }
    },
    async checkDomain () {
      if (!this.project.domain) {
        this.loading = false
        return false
      }

      const btn = this.$refs.checkBtn
      if (btn) {
        btn.classList.add('spin-reverse')
        setTimeout(() => {
          btn.classList.remove('spin-reverse')
        }, 1000)
      }

      if (this.awsConnected) {
        this.awsNS = []
        const {data} = await axios.get(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/add-domain`)
        data.data.forEach(item => {
          this.awsNS.push({
            name: item,
            copied: false
          })
        })
      }

      const awsNameservers = this.awsNS.map(item => item.name.replace(/\.$/, ''))

      await this.checkNS(awsNameservers)
        .then(async (data) => {
          this.pendingSetup = data.pending
          this.verify = data.valid
          this.awsVerified = data.awsValid

          if (data.valid && (data.pending || this.project?.aws_data?.tenant_id)) {
            this.step = 'management'
            if (this.awsConnected) {
              await this.fetchDNSRecordsAws()
            } else {
              await this.fetchDNSRecords()
            }
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }
        })
      this.loading = false
    },
    async connectDomainAdmin () {
      try {
        await this.setupAws()
          .then(async () => {
            await this.refreshState()
            this.setupDomainModal = false
          })
      } catch (error) {
        console.log('Error during AWS setup:', error)
        this.setupDomainModal = false
        this.loading = false
      }
    },
    async connectDomain () {
      this.loading = true
      this.setupDomainModal = true
      this.startLoadingDots()

      await axios.get(`api/projects/${this.project.id}/bunny-setup`)
        .then(async () => {
          setTimeout(async () => {
            try {
              await Promise.all([
                axios.get(`api/projects/${this.project.id}/bunny-setup/ssl?type=main`),
                axios.get(`api/projects/${this.project.id}/bunny-setup/ssl?type=social`),
                axios.get(`api/projects/${this.project.id}/bunny-setup/ssl?type=community`),
                axios.get(`api/projects/${this.project.id}/bunny-setup/ssl?type=www`)
              ])
              this.stopLoadingDots()
              await this.refreshState()
              this.setupDomainModal = false
            } catch (err) {
              console.error('SSL setup failed:', err)
              this.loading = false
              this.setupDomainModal = false
              this.$swal({
                icon: 'error',
                iconColor: '#e34f5b',
                toast: true,
                position: 'top-right',
                title: 'One or more SSL setups failed.',
                showConfirmButton: false,
                timer: 5000
              })
            }
          }, 40000)
        })
        .catch((err) => {
          console.log(err)
          this.loading = false
          this.setupDomainModal = false
          this.$swal({
            icon: 'error',
            iconColor: '#e34f5b',
            toast: true,
            position: 'top-right',
            title: 'Server error',
            text: 'Please try again later or let us know about your problem.',
            showConfirmButton: false,
            timer: 5000
          })
        })
    },
    async connectDomainWithAws () {
      this.loading = true
      this.setupDomainModal = true
      this.startLoadingDots()

      await axios.get(`api/projects/${this.project.id}/bunny-setup`)
        .then(async () => {
          try {
            await this.setupAws()
              .then(async () => {
                await this.refreshState()
                this.setupDomainModal = false
              })
          } catch (err) {
            console.error('SSL setup failed:', err)
            this.loading = false
            this.setupDomainModal = false
            this.$swal({
              icon: 'error',
              iconColor: '#e34f5b',
              toast: true,
              position: 'top-right',
              title: 'One or more SSL setups failed.',
              showConfirmButton: false,
              timer: 5000
            })
          }
        })
        .catch((err) => {
          console.log(err)
          this.loading = false
          this.setupDomainModal = false
          this.$swal({
            icon: 'error',
            iconColor: '#e34f5b',
            toast: true,
            position: 'top-right',
            title: 'Server error',
            text: 'Please try again later or let us know about your problem.',
            showConfirmButton: false,
            timer: 5000
          })
        })
    },
    async connectAWSDNS () {
      this.loading = true
      this.setupDomainModal = true
      this.awsDNSModal = false
      this.startLoadingDots()

      await axios.get(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/migrate`)
        .then(async () => {
          this.stopLoadingDots()
          this.$swal({
            icon: 'success',
            title: 'DNS Migration Successful',
            showConfirmButton: false,
            timer: 3000
          })
          await this.refreshState()
          this.setupDomainModal = false
        })
    },
    async upgradeAWS () {
      try {
        await this.setupAws()
          .then(async () => {
            await axios.get(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/migrate`)
            await this.refreshState()
            this.setupDomainModal = false
          })
      } catch (error) {
        console.log('Error during AWS upgrade:', error)
        this.setupDomainModal = false
        this.loading = false
      }
    },
    async upgradeAWSDNS () {
      await axios.get(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/add-domain`)
        .then(async ({data}) => {
          this.awsNS = []
          data.data.forEach(item => {
            this.awsNS.push({
              name: item,
              copied: false
            })
          })
          this.awsDNSModal = true
        })
    },
    async fetchDNSRecords () {
      this.loading = true
      await this.fetchDNS().then(async (data) => {
        const findSSL = item => {
          if (!item) return false
          const res = data.ssl.find(s => {
            if (item.Name === '') return s
            else return s.Name === item.Name
          })
          if (!res || !res.Value) return false
          return !res.Value.startsWith('subject=CN = *.b-cdn.net')
        }
        const regexPublic = /^public\d+\.b-cdn\.net$/
        const regexCommunity = /^community\d+\.b-cdn\.net$/

        if (data.Records && Array.isArray(data.Records)) {
          this.records = data.Records
            .filter(item => item && item.Type === 2 && (regexPublic.test(item.Value) || regexCommunity.test(item.Value) || ['d2b77328-b.b-cdn.net', 'd094fdac-8.b-cdn.net'].includes(item.Value)))
            .map(item => {
              if (!item) return null
              return {
                ...item,
                Ssl: findSSL(item),
                ModuleType: regexPublic.test(item.Value) || ['d094fdac-8.b-cdn.net'].includes(item.Value) ? 'Website' :
                  regexCommunity.test(item.Value) || ['d2b77328-b.b-cdn.net'].includes(item.Value) ? 'Community' : ''
              }
            })
            .filter(item => this.socialActivated ? true : item?.ModuleType !== 'Community')
            .filter(Boolean)
        }

        if (data.ssl && Array.isArray(data.ssl)) {
          for (const item of data.ssl) {
            if (!item || !item.Value || item?.Value?.startsWith('subject=CN = *.b-cdn.net')) {
              this.sslHostname = item?.Name || ''
              this.sslModal = true
              await axios.get(`api/projects/${this.project.id}/bunny-setup/ssl${item?.Name === this.project.domain ? '?type=main' : `?type=${item?.Name}`}`)
              const inx = this.records.findIndex(t => t?.Name === item?.Name)
              if (inx !== -1 && this.records[inx]) {
                this.records[inx].Ssl = true
              }
              this.sslModal = false
            }
          }
        }
      }).finally(() => this.loading = false)
    },
    async fetchDNSRecordsAws () {
      this.loading = true
      try {
        const {data} = await axios.get(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/records`)
        this.awsDnsMigrated = data.data.find(record => ['A', 'CNAME'].includes(record.Type))

        if (this.awsDnsMigrated) {
          const [{ data: communityData }] = await Promise.all([
            axios.get(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/get-tenant?type=community`)
          ])

          const communityDomains = communityData?.data?.Domains?.map(d => d.Domain) || []
          let allowedDomains = []

          if (this.socialActivated) {
            allowedDomains = [
              this.project.domain,
              `www.${this.project.domain}`,
              ...communityDomains
            ]
          } else {
            allowedDomains = [
              this.project.domain,
              `www.${this.project.domain}`
            ]
          }

          const records = data.data
            .map(record => {
              let value
              let priority
              if (record.Type === 'A') {
                value = record.AliasTarget ? record.AliasTarget.DNSName : record.ResourceRecords[0].Value
              } else if (record.Type === 'AAAA') {
                value = record.ResourceRecords[0].Value
              } else if (record.Type === 'CNAME') {
                value = record.ResourceRecords[0].Value
              } else if (record.Type === 'TXT') {
                value = record.ResourceRecords[0].Value.slice(1, -1)
              } else if (record.Type === 'MX') {
                const mx = record.ResourceRecords[0].Value.split(' ')
                priority = mx[0]
                value = mx[1]
              }

              const domain = record.Name.slice(0, -1) === this.project.domain ? '' : record.Name.split('.')[0]
              const ModuleType = communityDomains.includes(record.Name.slice(0, -1)) ? 'Community' : 'Website'

              return {
                Type: record.Type,
                Ttl: record.TTL ? record.TTL / 60 : undefined,
                Value: value,
                Priority: priority,
                Domain: record.Name.slice(0, -1),
                Name: domain,
                Ssl: true,
                ModuleType,
                solid: record
              }
            })
            .filter(record => allowedDomains.includes(record.Domain))

          this.records = _.uniqBy(records, 'Name')

          this.loading = false
        } else {
          const [dnsData, { data: communityData }] = await Promise.all([
            this.fetchDNS(),
            axios.get(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/get-tenant?type=community`)
          ])

          const communityDomains = communityData?.data?.Domains?.map(d => d.Domain) || []
          const allowedDomains = [
            this.project.domain,
            `www.${this.project.domain}`,
            ...communityDomains
          ]

          this.records = (dnsData.Records || [])
            .filter(record => {
              const fullName = record.Name ? `${record.Name}.${this.project.domain}` : this.project.domain
              return record.Type === 2 && allowedDomains.includes(fullName)
            })
            .map(record => ({
              ...record,
              Ssl: true,
              ModuleType: communityDomains.includes(`${record.Name}.${this.project.domain}`) ? 'Community' : 'Website'
            }))
        }
      } catch (error) {
        console.error('Error fetching AWS DNS records:', error)
      } finally {
        this.loading = false
      }
    },
    async checkCommunityActivation () {
      this.loading = true

      try {
        const res = await fetch(`https://${this.project.subdomain}.estage.site/api/social/${this.project.id}/check?project=${this.project.id}`)

        const data = await res.json().catch(() => null)

        if (data && typeof data.created !== 'undefined') {
          this.socialActivated = !!data.created
        }
      } catch (err) {
        console.warn('checkCommunityActivation error:', err)
        this.socialActivated = false
      } finally {
        this.loading = false
      }
    },
    editItem (item) {
      this.domainModal = true
      this.form = {
        id: item.Id,
        name: item.Name,
        value: item.Value,
        port: item.Port,
        priority: item.Priority,
        weight: item.Weight,
        type: item.Type,
        ttl: item.Ttl,
        flags: item.Flags,
        tag: item.Tag,
        monitor_type: item.MonitorType,
        smart_routing_type: item.SmartRoutingType,
        enable: !item.Disabled,
        solid: item.solid
      }
    },
    async addRecord () {
      this.loading = true
      let payload = {}

      if ([0, 1, 2].includes(this.form.type)) {
        payload = {
          weight: this.form.weight,
          monitor_type: this.form.monitor_type,
          smart_routing_type: this.form.smart_routing_type,
          disabled: !this.form.enable
        }
      }

      if (this.form.type === 4) {
        payload = {
          priority: this.form.priority
        }
      }

      if (this.form.type === 9) {
        payload = {
          flags: this.form.flags,
          tag: this.form.tag
        }
      }

      const endpoint = this.awsConnected
        ? `${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`
        : `api/projects/${this.project.id}/bunny-dns-records`

      await axios.post(endpoint, {
        name: this.form.name,
        type: this.form.type,
        value: this.form.value,
        ttl: ![5, 11].includes(this.form.ttl) ? this.form.ttl : undefined,
        disabled: [0, 1, 2].includes(this.form.type) ? !this.form.enable : false,
        ...payload
      })
        .then(({data}) => {
          if (data.data?.ErrorKey === 'validation_error') {
            this.$swal({
              icon: 'error',
              title: 'Server error',
              text: 'Please try again later or let us know about your problem.',
              showConfirmButton: false,
              timer: 3500
            })
          }
        })
        .catch((err) => {
          console.log(err)
          this.$swal({
            icon: 'error',
            title: 'Server error',
            text: 'Please try again later or let us know about your problem.',
            showConfirmButton: false,
            timer: 3500
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async deleteRecord (item) {
      this.loading = true
      if (this.awsConnected) {
        const record = item.solid

        const type = this.allTypes.find(type => type.name === record.Type)

        let value
        if (record.Type === 'A') {
          value = record.AliasTarget ? record.AliasTarget.DNSName : record.ResourceRecords[0].Value
        } else if (record.Type === 'AAAA') {
          value = record.ResourceRecords[0].Value
        } else if (record.Type === 'CNAME') {
          value = record.ResourceRecords[0].Value
        } else if (record.Type === 'TXT') {
          value = record.ResourceRecords[0].Value
        } else if (record.Type === 'MX') {
          value = record.ResourceRecords[0].Value
        }

        await axios.delete(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`, {
          data: {
            name: record.Name,
            type: type.value,
            value: value,
            ttl: Number(record.TTL)
          }
        })
          .then(() => {
            this.$swal({
              icon: 'success',
              title: 'DNS Record successfully deleted',
              toast: true,
              position: 'top-right',
              showConfirmButton: false,
              timer: 3000
            })
            this.fetchDNSRecords()
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        await axios.delete(`api/projects/${this.project.id}/bunny-dns/${item.Id}`)
          .then(() => {
            this.$swal({
              icon: 'success',
              title: 'DNS Record successfully deleted',
              toast: true,
              position: 'top-right',
              showConfirmButton: false,
              timer: 3000
            })
            this.fetchDNSRecords()
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    async updateRecord () {
      // if (!this.form.id) return
      this.loading = true
      if (this.awsConnected && this.awsVerified) {
        const record = this.form.solid

        const type = this.allTypes.find(type => type.name === record.Type)

        let value
        let priority
        if (record.Type === 'A') {
          value = record.AliasTarget ? record.AliasTarget.DNSName : record.ResourceRecords[0].Value
        } else if (record.Type === 'AAAA') {
          value = record.ResourceRecords[0].Value
        } else if (record.Type === 'CNAME') {
          value = record.ResourceRecords[0].Value
        } else if (record.Type === 'TXT') {
          value = record.ResourceRecords[0].Value.slice(1, -1)
        } else if (record.Type === 'MX') {
          const mx = record.ResourceRecords[0].Value.split(' ')
          priority = mx[0]
          value = mx[1]
        }

        await axios.delete(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`, {
          data: {
            name: record.Name,
            type: type.value,
            value: value,
            ttl: record.TTL ? Number(record.TTL) : 0,
            priority: priority
          }
        })

        let payload = {}

        if ([0, 1, 2].includes(this.form.type)) {
          payload = {
            weight: this.form.weight,
            monitor_type: this.form.monitor_type,
            smart_routing_type: this.form.smart_routing_type,
            disabled: !this.form.enable
          }
        }

        if (this.form.type === 4) {
          payload = {
            priority: this.form.priority
          }
        }

        if (this.form.type === 9) {
          payload = {
            flags: this.form.flags,
            tag: this.form.tag
          }
        }

        const recordType = this.allTypes.find(type => type.name === this.form.type)

        await axios.post(`${this.newAPI}/api/projects/${this.project.id}/aws-route-53/record`, {
          name: this.form.name + '.' + this.project.domain,
          type: recordType.value,
          value: this.form.value,
          ttl: 0,
          ...payload
        })
          .then(({data}) => {
            if (data.data?.ErrorKey === 'validation_error') {
              this.$swal({
                icon: 'error',
                title: 'Server error',
                text: 'Please try again later or let us know about your problem.',
                showConfirmButton: false,
                timer: 3500
              })
            } else {
              this.fetchDNSRecordsAws()
              this.setupDomainModal = false
              this.$swal({
                icon: 'success',
                title: 'DNS Record successfully updated',
                toast: true,
                position: 'top-right',
                showConfirmButton: false,
                timer: 1500
              })
            }
          })
          .catch((err) => {
            console.log(err)
            this.$swal({
              icon: 'error',
              title: 'Server error',
              text: 'Please try again later or let us know about your problem.',
              showConfirmButton: false,
              timer: 3500
            })
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        await axios.delete(`api/projects/${this.project.id}/bunny-dns/${this.form.id}`)
        await axios.post(`api/projects/${this.project.id}/bunny-dns-records`, {
          name: this.form.name,
          type: 2,
          value: this.form.value,
          ttl: 15,
          disabled: false,
          weight: 0,
          monitor_type: 0,
          smart_routing_type: 0
        })
          .then(async ({data}) => {
            if (data.data?.ErrorKey === 'validation_error') {
              this.$swal({
                icon: 'error',
                title: 'Server error',
                text: 'Please try again later or let us know about your problem.',
                showConfirmButton: false,
                timer: 3500
              })
            } else {
              if (this.awsConnected) {
                await axios.put(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/edit-tenant`, {
                  type: 'social',
                  name: this.form.name
                })
                  .then(async () => {
                    this.domainModal = false
                    this.$swal({
                      icon: 'success',
                      title: 'Subdomain successfully updated',
                      showConfirmButton: false,
                      timer: 1500
                    })
                    if (this.awsConnected) {
                      await this.fetchDNSRecordsAws()
                    } else {
                      await this.fetchDNSRecords()
                    }
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  })
                  .catch((err) => {
                    console.log(err)
                    this.$swal({
                      icon: 'error',
                      title: 'Server error',
                      text: 'Please try again later or let us know about your problem.',
                      showConfirmButton: false,
                      timer: 3500
                    })
                  })
                  .finally(() => {
                    this.loading = false
                    this.domainModal = false
                  })
              } else {
                this.domainModal = false
                this.$swal({
                  icon: 'success',
                  title: 'Subdomain successfully updated',
                  showConfirmButton: false,
                  timer: 1500
                })
                if (this.awsConnected) {
                  await this.fetchDNSRecordsAws()
                } else {
                  await this.fetchDNSRecords()
                }
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }
            }
          })
          .catch((err) => {
            console.log(err)
            this.$swal({
              icon: 'error',
              title: 'Server error',
              text: 'Please try again later or let us know about your problem.',
              showConfirmButton: false,
              timer: 3500
            })
            this.loading = false
            this.domainModal = false
          }) 
      }
    },
    async checkCertificate (maxRetries = 3, delayMs = 10000) {
      let attempt = 0

      const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms))

      while (attempt < maxRetries) {
        try {
          const { data } = await axios.get(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/status-certificate`)

          if (data?.data?.status === 'ISSUED') {
            await axios.get(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/create-tenant`)
            return true
          }

          attempt++
          if (attempt < maxRetries) {
            await wait(delayMs)
          }
        } catch (error) {
          console.error(`checkCertificate attempt ${attempt + 1} failed`, error)
          attempt++
          if (attempt < maxRetries) {
            await wait(delayMs)
          }
        }
      }
      return false
    },
    async checkCertificateSave (maxRetries = 3, delayMs = 10000) {
      let attempt = 0

      const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms))

      while (attempt < maxRetries) {
        try {
          const { data } = await axios.get(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/status-certificate`)

          if (data?.data?.status === 'ISSUED') {
            try {
              await axios.get(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/create-tenant`)
              await this.refreshState()
            } catch (error) {
              const msg = error?.response?.data?.message

              if (msg === 'public_tenant_error' || msg === 'community_tenant_error' || msg === 'tenant_not_found') {
                try {
                  if (!this.project?.aws_data?.tenant_id) {
                    await axios.get(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/search`)
                  }
                  if (!this.project?.aws_data?.community_tenant_id) {
                    await axios.get(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/search?type=social`)
                  }
                  await this.refreshState()
                } catch (searchError) {
                  console.error('Search fallback failed:', searchError)
                }
              } else {
                console.error('Unexpected error:', error)
              }
            }
            return true
          }

          attempt++
          if (attempt < maxRetries) {
            await wait(delayMs)
          }
        } catch (error) {
          console.error(`checkCertificate attempt ${attempt + 1} failed`, error)
          attempt++
          if (attempt < maxRetries) {
            await wait(delayMs)
          }
        }
      }
      return false
    },
    async setupAws () {
      this.loading = true
      this.awsUpgradeModal = false
      this.setupDomainModal = true
      this.startLoadingDots()

      try {
        await this.$store.dispatch('auth/me')
        await axios.get(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/create-certificate`)

        // Wait 10 seconds for the certificate to be generated
        await new Promise(resolve => setTimeout(resolve, 10000))

        await axios.get(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/verify-certificate`)

        // Wait another 10 seconds for DNS verification to propagate
        await new Promise(resolve => setTimeout(resolve, 10000))

        const certificateReady = await this.checkCertificate()

        if (!certificateReady) {
          throw new Error('Certificate not issued in time')
        }

      } catch (error) {
        console.error('setupAws error:', error)
        this.$swal({
          icon: 'error',
          iconColor: '#e34f5b',
          toast: true,
          position: 'top-right',
          title: 'Server error',
          text: 'Please try again later or let us know about your problem.',
          showConfirmButton: false,
          timer: 5000
        })
      } finally {
        this.stopLoadingDots()
      }
    },
    async submitRecord () {
      this.form.id || this.form.solid ? await this.updateRecord() : await this.addRecord()
    }
  },
  async created () {
    this.initLoading = true
    await this.checkCommunityActivation()
    if (this.project.domain) {
      this.domain = this.project.domain
      this.domainSaved = true
    } else {
      this.loading = false
    }
    await this.checkDomain()
    this.initLoading = false
  },
  async mounted () {
    await this.$store.dispatch('projects/hasAccessToPage', 'domain_management')

    if (this.awsConnected && (!this.project.aws_data?.tenant_id || !this.project.aws_data?.community_tenant_id)) {
      this.initLoading = true
      await axios.get(`${this.newAPI}/api/projects/${this.project.id}/cloud-front/verify-certificate`)
        .then(async () => {
          await this.checkCertificateSave()
            .then(async () => {
              await this.checkDomain()
              this.initLoading = false
            })
            .catch((error) => {
              console.log(error)
              this.initLoading = false
            })
        })
    }
  }
}
</script>

<style>
@keyframes spin-reverse {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(-360deg); }
}

.spin-reverse {
  animation: spin-reverse 1s linear infinite;
}
</style>